import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';
import { escapeIlikeTerm } from '../ilike';

export type AdminSubmissionRow = Database['public']['Tables']['seller_submissions']['Row'];
export type AdminSubmissionStatus = Database['public']['Enums']['submission_status'];
export type SellerSubmissionPhotoRow =
  Database['public']['Tables']['seller_submission_photos']['Row'];

type TreatedByJoin = Pick<
  Database['public']['Tables']['users']['Row'],
  'id' | 'first_name' | 'last_name' | 'email' | 'role'
>;

type ConvertedDealJoin = Pick<
  Database['public']['Tables']['deals']['Row'],
  'id' | 'reference_id' | 'slug' | 'status'
>;

export type AdminSubmissionRowJoined = AdminSubmissionRow & {
  treated_by_user: TreatedByJoin | null;
  converted_deal: ConvertedDealJoin | null;
  photos_count: number;
};

export type AdminSubmissionDetail = AdminSubmissionRowJoined & {
  photos: SellerSubmissionPhotoRow[];
};

export type AdminSubmissionsStatusFilter = 'all' | AdminSubmissionStatus;

export type ListAdminSubmissionsParams = {
  status?: AdminSubmissionsStatusFilter;
  searchQuery?: string;
  pageSize?: number;
  offset?: number;
};

export type ListAdminSubmissionsResult = {
  rows: AdminSubmissionRowJoined[];
  total: number;
};

const DEFAULT_PAGE_SIZE = 25;

const SELECT_SUBMISSION_LIST =
  '*, treated_by_user:treated_by (id, first_name, last_name, email, role), converted_deal:converted_deal_id (id, reference_id, slug, status), photos_count:seller_submission_photos(count)';

const SELECT_SUBMISSION_DETAIL =
  '*, treated_by_user:treated_by (id, first_name, last_name, email, role), converted_deal:converted_deal_id (id, reference_id, slug, status), photos:seller_submission_photos (id, submission_id, storage_key, position, size_bytes, mime_type, created_at)';

const FSM_TRANSITIONS: Record<AdminSubmissionStatus, AdminSubmissionStatus[]> = {
  new: ['in_progress', 'accepted', 'refused'],
  in_progress: ['accepted', 'refused'],
  accepted: [],
  refused: [],
};

/**
 * Forward-only transitions allowed from the given status. Mirror of the
 * `seller_submissions_status_forward` SQL function (20260508120000_seller_submissions.sql
 * lines 343-371). Used by the admin drawer dropdown to only render valid
 * targets — the trigger remains the source of truth and raises 23514 on a
 * bypass attempt.
 */
export function nextAllowedSubmissionStatuses(
  current: AdminSubmissionStatus,
): AdminSubmissionStatus[] {
  return FSM_TRANSITIONS[current];
}

type RawListRow = Omit<AdminSubmissionRowJoined, 'photos_count'> & {
  photos_count: Array<{ count: number }> | { count: number } | number | null;
};

function flattenPhotosCount(raw: RawListRow['photos_count']): number {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  if (Array.isArray(raw)) return raw[0]?.count ?? 0;
  return raw.count ?? 0;
}

/**
 * List admin-visible seller submissions with optional status filter.
 * Embeds `treated_by_user` (admin who triaged), `converted_deal` (Draft
 * created on accept — Patch 6) and a `photos_count` aggregate. RLS:
 * `seller_submissions_admin_select` (20260508120000 lines 591-595) — only
 * admin / super_admin see rows.
 */
export async function listAdminSubmissions(
  params: ListAdminSubmissionsParams = {},
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<ListAdminSubmissionsResult> {
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const offset = params.offset ?? 0;

  let q = client.from('seller_submissions').select(SELECT_SUBMISSION_LIST, { count: 'exact' });

  if (params.status && params.status !== 'all') {
    q = q.eq('status', params.status);
  }

  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    const term = escapeIlikeTerm(params.searchQuery.trim());
    if (term.length > 0) {
      // Own columns only — seller contact lives inline on the submission
      // (sellers are not authenticated users), so the search covers the
      // useful triage fields without any cross-table OR.
      q = q.or(
        `reference_id.ilike.%${term}%,seller_full_name.ilike.%${term}%,seller_email.ilike.%${term}%,zone.ilike.%${term}%,community.ilike.%${term}%,building.ilike.%${term}%`,
      );
    }
  }

  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range(offset, offset + pageSize - 1);

  if (error) throw error;

  const raw = (data ?? []) as unknown as RawListRow[];
  const rows: AdminSubmissionRowJoined[] = raw.map((r) => ({
    ...(r as unknown as AdminSubmissionRowJoined),
    photos_count: flattenPhotosCount(r.photos_count),
  }));

  return { rows, total: count ?? 0 };
}

/**
 * GLOBAL count of `new` submissions — deliberately independent of the
 * page AND of the active filters (ADR-056): the "new" badge is a triage
 * signal, not a property of the current view. Never derive it from the
 * paginated rows.
 */
export async function countAdminSubmissionsNew(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<number> {
  const { count, error } = await client
    .from('seller_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'new');

  if (error) throw error;
  return count ?? 0;
}

/**
 * Fetch a single seller submission with full photo rows ordered by
 * `position`. Returns `null` if no row matches (or RLS hides it). Used by
 * the admin drawer to load the lightbox + property + contact + history.
 */
export async function getAdminSubmissionById(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminSubmissionDetail | null> {
  const { data, error } = await client
    .from('seller_submissions')
    .select(SELECT_SUBMISSION_DETAIL)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const raw = data as unknown as AdminSubmissionDetail & {
    photos: SellerSubmissionPhotoRow[] | null;
  };
  const photos = (raw.photos ?? []).slice().sort((a, b) => a.position - b.position);

  return {
    ...raw,
    photos,
    photos_count: photos.length,
  };
}

export type UpdateSubmissionStatusParams = {
  id: string;
  toStatus: AdminSubmissionStatus;
  refusalReason?: string;
};

/**
 * Transition a submission to a new status via plain UPDATE. The DB trigger
 * `seller_submissions_status_forward_trg` (20260508120000 lines 373-375)
 * enforces forward-only — a forbidden transition surfaces as PostgrestError
 * `code = '23514'` (check_violation), propagated to the caller. The UI
 * matches on that code to show a localized toast.
 *
 * Side effects in the same transaction:
 *  - `seller_submissions_log_status_change_trg` writes the
 *    `seller_submission_status_changed` audit_log row.
 *  - `seller_submissions_touch_updated_at_trg` advances `updated_at`.
 *
 * `treated_by` is overwritten with `auth.uid()` at every transition (the
 * latest admin who took action — Spec §M11 leaves the semantics ambiguous
 * and the audit_log row carries the actor anyway). `treated_at` is set to
 * `clock_timestamp()` server-side to align with the trigger.
 */
export async function updateSubmissionStatus(
  params: UpdateSubmissionStatusParams,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminSubmissionRowJoined> {
  const { id, toStatus, refusalReason } = params;

  const { data: userResult, error: userError } = await client.auth.getUser();
  if (userError) throw userError;
  if (!userResult?.user) throw new Error('Not authenticated.');

  const update: Database['public']['Tables']['seller_submissions']['Update'] = {
    status: toStatus,
    treated_by: userResult.user.id,
    treated_at: new Date().toISOString(),
  };
  if (toStatus === 'refused') {
    update.refusal_reason = refusalReason ?? null;
  }

  const { data, error } = await client
    .from('seller_submissions')
    .update(update)
    .eq('id', id)
    .select(SELECT_SUBMISSION_LIST)
    .single();

  if (error) throw error;

  const raw = data as unknown as RawListRow;
  return {
    ...(raw as unknown as AdminSubmissionRowJoined),
    photos_count: flattenPhotosCount(raw.photos_count),
  };
}

// ---------------------------------------------------------------------------
// acceptSubmission — Patch 6 (Edge accept-seller-submission)
// ---------------------------------------------------------------------------
// Invokes the admin-only Edge function which atomically creates a Draft deal
// (via the SECURITY DEFINER RPC accept_seller_submission), queues the
// seller_submission_accepted email and copies the submission photos from
// `seller-photos` to `deal-photos`. The locale used by the email comes from
// `seller_submissions.preferred_language` captured at the public submit —
// no `locale` parameter on this client (ADR-024).
//
// Photo copy is sync: the Edge waits for all photos before returning, and
// the response carries the final `photos_copy_status` ('ok' | 'none' on
// success, 'failed' with `warning: 'photo_copy_failed'` on rollback).

export type AcceptSubmissionResult = {
  deal_id: string;
  deal_reference_id: string;
  deal_slug: string;
  deal_title: string;
  photos_copy_status: 'ok' | 'failed' | 'none';
  warning?: 'photo_copy_failed';
};

export type AcceptSubmissionError =
  | 'non_admin'
  | 'submission_not_found'
  | 'forbidden_status_transition'
  | 'unauthenticated'
  | 'internal_error';

export class AcceptSubmissionFailure extends Error {
  constructor(
    public readonly errorCode: AcceptSubmissionError,
    message?: string,
  ) {
    super(message ?? errorCode);
    this.name = 'AcceptSubmissionFailure';
  }
}

/**
 * Accept a seller submission and create a Draft deal in one Edge invocation.
 * Resolves with `photos_copy_status` to let the UI surface a warning toast
 * when the photo copy partially failed (Draft is still created in that case).
 * Rejects with `AcceptSubmissionFailure` carrying a stable `errorCode` for
 * i18n toast mapping.
 */
export async function acceptSubmission(
  params: { id: string },
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AcceptSubmissionResult> {
  const { data, error } = await client.functions.invoke<{
    success: boolean;
    code?: AcceptSubmissionError;
    deal_id?: string;
    deal_reference_id?: string;
    deal_slug?: string;
    deal_title?: string;
    photos_copy_status?: 'ok' | 'failed' | 'none';
    warning?: 'photo_copy_failed';
    message?: string;
  }>('accept-seller-submission', {
    body: { submission_id: params.id },
  });

  if (error) {
    // FunctionsHttpError surfaces the response body in `context`; fall back
    // to a generic internal_error if the shape is unexpected.
    const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const payload = (await ctx.json()) as { code?: AcceptSubmissionError };
        if (payload?.code) {
          throw new AcceptSubmissionFailure(payload.code, error.message);
        }
      } catch (parseErr) {
        if (parseErr instanceof AcceptSubmissionFailure) throw parseErr;
        // Swallow parse error, fall through to generic internal_error below.
      }
    }
    throw new AcceptSubmissionFailure('internal_error', error.message);
  }

  if (!data || data.success !== true) {
    throw new AcceptSubmissionFailure(
      data?.code ?? 'internal_error',
      data?.message ?? 'unexpected_response',
    );
  }

  if (
    !data.deal_id ||
    !data.deal_reference_id ||
    !data.deal_slug ||
    !data.deal_title ||
    !data.photos_copy_status
  ) {
    throw new AcceptSubmissionFailure('internal_error', 'missing_fields_in_edge_response');
  }

  return {
    deal_id: data.deal_id,
    deal_reference_id: data.deal_reference_id,
    deal_slug: data.deal_slug,
    deal_title: data.deal_title,
    photos_copy_status: data.photos_copy_status,
    warning: data.warning,
  };
}
